import type { TFunction } from "i18next";
import { CollaborationCancellationReasonCode } from "@/interface/collaborationCancellation";

type ApiCancellationReasonCode =
  | "personal_circumstances"
  | "content_requirements"
  | "payment_issue"
  | "need_more_time"
  | "product_or_shipping_issue"
  | "other";

interface CancellationReasonDefinition {
  code: CollaborationCancellationReasonCode;
  apiCode: ApiCancellationReasonCode;
  labelKey: string;
  labelDefaultValue: string;
}

const cancellationReasonDefinitions: CancellationReasonDefinition[] = [
  {
    code: CollaborationCancellationReasonCode.PersonalCircumstances,
    apiCode: "personal_circumstances",
    labelKey:
      "campaignDetail.collaborationCancellation.reasons.personalCircumstances",
    labelDefaultValue: "Personal circumstances prevent me from continuing",
  },
  {
    code: CollaborationCancellationReasonCode.ContentRequirementsExpanded,
    apiCode: "content_requirements",
    labelKey:
      "campaignDetail.collaborationCancellation.reasons.contentRequirementsExpanded",
    labelDefaultValue: "The content requirements are more than I expected",
  },
  {
    code: CollaborationCancellationReasonCode.PaymentNoLongerWorks,
    apiCode: "payment_issue",
    labelKey:
      "campaignDetail.collaborationCancellation.reasons.paymentNoLongerWorks",
    labelDefaultValue: "The payment no longer works for me",
  },
  {
    code: CollaborationCancellationReasonCode.NeedMoreTime,
    apiCode: "need_more_time",
    labelKey: "campaignDetail.collaborationCancellation.reasons.needMoreTime",
    labelDefaultValue: "I need more time",
  },
  {
    code: CollaborationCancellationReasonCode.ProductOrShippingIssue,
    apiCode: "product_or_shipping_issue",
    labelKey:
      "campaignDetail.collaborationCancellation.reasons.productOrShippingIssue",
    labelDefaultValue: "There is a product or shipping issue",
  },
  {
    code: CollaborationCancellationReasonCode.Other,
    apiCode: "other",
    labelKey: "campaignDetail.collaborationCancellation.reasons.other",
    labelDefaultValue: "Other",
  },
];

const definitionsByApiCode = new Map(
  cancellationReasonDefinitions.map((definition) => [
    definition.apiCode,
    definition,
  ]),
);

const definitionsByUiCode = new Map(
  cancellationReasonDefinitions.map((definition) => [
    definition.code,
    definition,
  ]),
);

const creatorAccountableReasonCodes =
  new Set<CollaborationCancellationReasonCode>([
    CollaborationCancellationReasonCode.PersonalCircumstances,
    CollaborationCancellationReasonCode.ContentRequirementsExpanded,
    CollaborationCancellationReasonCode.PaymentNoLongerWorks,
    CollaborationCancellationReasonCode.NeedMoreTime,
  ]);

export const fromApiCancellationReasonCode = (
  value: unknown,
): CollaborationCancellationReasonCode => {
  if (typeof value !== "string") {
    return CollaborationCancellationReasonCode.Other;
  }

  const apiDefinition = definitionsByApiCode.get(
    value as ApiCancellationReasonCode,
  );
  return apiDefinition?.code || CollaborationCancellationReasonCode.Other;
};

export const getCancellationReasonLabel = (
  code: CollaborationCancellationReasonCode,
  t: TFunction,
) => {
  const definition = definitionsByUiCode.get(code);
  return definition
    ? t(definition.labelKey, {
        defaultValue: definition.labelDefaultValue,
      })
    : "";
};

export const isCreatorAccountableCancellationReason = (
  code: CollaborationCancellationReasonCode,
) => creatorAccountableReasonCodes.has(code);
